import type { ActivitiesData, ActivityItem } from '@atlassian/recent-work-client/query';
import { createActivityClient } from '@atlassian/recent-work-client/activity-client';
import type { ActivityClient } from '@atlassian/recent-work-client/base';
import type { ActivityFilters } from '@atlassian/recent-work-client/activity-filters';
import type { ActivityQuery } from '@atlassian/recent-work-client/activity-queries';

import { createPlatformSiteAri } from '@townsquare/collab-context/create-site-ari';

import { postFetchFilterItemsByCloudIds } from './post-fetch-cloud-id-filter';
import { EditorActivityItem, getWorkedOnAndViewedItemsResults } from './types';

const ACTIVITIES_LIMIT = 50;
const FETCH_ACTIVITIES_DEFAULT_RETRY_MS = [50, 125, 250];
const ROOT_CONTAINER_ID_LIMIT = 5;

export default class ActivityProvider {
	private activityClient: ActivityClient;

	constructor() {
		this.activityClient = createActivityClient('v3', 'atlas');
	}

	// TODO GCKO-514: Clean up this method once the your-apps refresh has been fully rolled out
	async getWorkedOnAndViewedItems({
		cloudIds,
	}: {
		cloudIds?: string[];
	}): Promise<getWorkedOnAndViewedItemsResults> {
		// Worklens has a max limit of 5 rootContainerIds, so if we have more than that we need to post-filter the results
		const shouldPostFetchFilter = cloudIds && cloudIds.length > ROOT_CONTAINER_ID_LIMIT;

		const siteAriFilters = cloudIds
			? {
					rootContainerIds: cloudIds
						.map((siteId) => createPlatformSiteAri(siteId))
						.filter((ari): ari is string => ari !== null),
				}
			: {};

		try {
			const activities = await this.activityClient.fetchActivities(
				FETCH_ACTIVITIES_DEFAULT_RETRY_MS,
				{ limit: ACTIVITIES_LIMIT, ...(shouldPostFetchFilter ? {} : siteAriFilters) },
				['workedOn', 'viewed'],
			);

			const postFetchFilteredViewedItems = shouldPostFetchFilter
				? postFetchFilterItemsByCloudIds(activities.viewed, cloudIds)
				: activities.viewed;
			const postFetchFilteredWorkedOnItems = shouldPostFetchFilter
				? postFetchFilterItemsByCloudIds(activities.workedOn, cloudIds)
				: activities.workedOn;

			return {
				data: {
					viewed: postFetchFilteredViewedItems,
					workedOn: postFetchFilteredWorkedOnItems,
				},
				error: false,
			};
		} catch (error) {
			return {
				data: {
					viewed: [],
					workedOn: [],
				},
				error: true,
			};
		}
	}

	async getWorkedOnAndViewedItemsWithPostFiltering({
		cloudIds,
	}: {
		cloudIds?: string[];
	}): Promise<getWorkedOnAndViewedItemsResults> {
		try {
			const activities = await this.activityClient.fetchActivities(
				FETCH_ACTIVITIES_DEFAULT_RETRY_MS,
				{ limit: ACTIVITIES_LIMIT },
				['workedOn', 'viewed'],
			);

			const postFetchFilteredViewedItems = cloudIds
				? postFetchFilterItemsByCloudIds(activities.viewed, cloudIds)
				: activities.viewed;
			const postFetchFilteredWorkedOnItems = cloudIds
				? postFetchFilterItemsByCloudIds(activities.workedOn, cloudIds)
				: activities.workedOn;

			return {
				data: {
					viewed: postFetchFilteredViewedItems,
					workedOn: postFetchFilteredWorkedOnItems,
				},
				error: false,
			};
		} catch (error) {
			return {
				data: {
					viewed: [],
					workedOn: [],
				},
				error: true,
			};
		}
	}

	async getRecentItemsWithoutAtlasObjects(): Promise<ActivityItem[]> {
		const activities = await this.activityClient.fetchActivities(
			FETCH_ACTIVITIES_DEFAULT_RETRY_MS,
			{ limit: ACTIVITIES_LIMIT, objectTypes: ['ISSUE', 'PAGE', 'BLOGPOST'] },
			['viewed'],
		);

		return activities.viewed;
	}

	// Used by the Editor
	async getRecentItems(): Promise<EditorActivityItem[]> {
		const activities = await this.activityClient.fetchActivities(
			FETCH_ACTIVITIES_DEFAULT_RETRY_MS,
			{ limit: ACTIVITIES_LIMIT },
			['viewed'],
		);

		return activities.viewed.map<EditorActivityItem>((i) => ({
			objectId: i.object.id,
			name: i.object.name || '',
			container: i.containers[0].name,
			url: i.object.url,
			iconUrl: i.object.icon?.value || '',
			type: i.object.type,
			viewedTimestamp: i.timestamp,
		}));
	}

	async searchFilteredRecent(search: string): Promise<ActivityItem[]> {
		const items = await this.getRecentItemsWithoutAtlasObjects();
		return this.filterActivityItems(items, search);
	}

	// Has to be EditorActivityItem supported due to the expected interface
	// from the Editor activityProvider prop (see src/ui/Editor/BaseEditor.tsx)
	async searchRecent(search: string): Promise<EditorActivityItem[]> {
		const items = await this.getRecentItems();
		return this.filterItems(items, search);
	}

	async fetchActivities(
		retryMS: number[],
		filters: ActivityFilters,
		activityQueries: ActivityQuery[],
	): Promise<ActivitiesData> {
		return this.activityClient.fetchActivities(retryMS, filters, activityQueries);
	}

	private filterItems(items: EditorActivityItem[], searchTerm: string) {
		if (searchTerm.length === 0) {
			return [];
		}

		searchTerm = searchTerm.toLowerCase();
		return items.filter((item) => {
			return Boolean(item.name.toLowerCase().includes(searchTerm.toLowerCase()));
		});
	}

	private filterActivityItems(items: ActivityItem[], searchTerm: string) {
		if (searchTerm.length === 0) {
			return [];
		}

		searchTerm = searchTerm.toLowerCase();
		return items.filter((item) => {
			return Boolean(item.object.name?.toLowerCase().includes(searchTerm.toLowerCase()));
		});
	}
}
